import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { DayjsFormats } from 'shared/lib'
import { CalendarFilterEnum, type ICalendarFilter } from 'shared/ui/CalendarDropdown/calendarEnum'

export interface ISendContactDate {
  from: Date | null
  to: Date | null
}

export class CalendarFilterStore {
  private _selectedCalendarFilter: ICalendarFilter = CalendarFilterEnum.allTime
  private _fromFilterDate: Date | null = null
  private _toFilterDate: Date | null = uiStore.dayjs().toDate()
  private _sendDate: ISendContactDate | null = null
  private _createdAt: string | null = null
  private _maxDate: Date = uiStore.dayjs().toDate()

  constructor(createdAt?: string | null) {
    this._createdAt = createdAt || null

    makeAutoObservable(this)
  }

  get maxDate() {
    return this._maxDate
  }

  get selectedCalendarFilter() {
    return this._selectedCalendarFilter
  }

  get fromFilterDate() {
    return this._fromFilterDate
  }

  get toFilterDate() {
    return this._toFilterDate
  }

  get isCustomMode() {
    return this._selectedCalendarFilter === CalendarFilterEnum.custom
  }

  get sendDate() {
    return this._sendDate
  }

  setSelectedCalendarFilter = (value: ICalendarFilter) => {
    this._selectedCalendarFilter = value

    switch (value) {
      case CalendarFilterEnum.last30days:
        this._fromFilterDate = uiStore.dayjs().subtract(30, 'd').toDate()
        this._toFilterDate = uiStore.dayjs().toDate()
        break
      case CalendarFilterEnum.thisMonth:
        this._fromFilterDate = uiStore.dayjs().date(1).toDate()
        this._toFilterDate = uiStore.dayjs().toDate()
        break
      case CalendarFilterEnum.lasMonth:
        this._fromFilterDate = uiStore.dayjs().subtract(1, 'M').date(1).toDate()
        this._toFilterDate = uiStore.dayjsWithoutTimezone().subtract(1, 'M').endOf('M').toDate()
        break
      case CalendarFilterEnum.last3month:
        this._fromFilterDate = uiStore.dayjs().subtract(3, 'M').date(1).toDate()
        this._toFilterDate = uiStore.dayjsWithoutTimezone().subtract(1, 'M').endOf('M').toDate()
        break
      case CalendarFilterEnum.last6month:
        this._fromFilterDate = uiStore.dayjs().subtract(6, 'M').date(1).toDate()
        this._toFilterDate = uiStore.dayjsWithoutTimezone().subtract(1, 'M').endOf('M').toDate()
        break
      case CalendarFilterEnum.lastYear:
        this._fromFilterDate = uiStore.dayjs().subtract(1, 'y').startOf('y').toDate()
        this._toFilterDate = uiStore.dayjsWithoutTimezone().subtract(1, 'y').endOf('y').toDate()
        break
      case CalendarFilterEnum.allTime:
        this._fromFilterDate = this._createdAt ? uiStore.dayjs(this._createdAt).toDate() : null
        this._toFilterDate = uiStore.dayjs().toDate()
        break
    }
  }

  setFromFilterDefaultDate = () => {
    this._fromFilterDate = this._createdAt ? uiStore.dayjs(this._createdAt).toDate() : null
  }

  setDateRangeValue = (from: Date | null, to: Date | null) => {
    this._fromFilterDate = from
    this._toFilterDate = to
  }

  setParamsDate = () => {
    if (!this._toFilterDate) {
      this._toFilterDate = uiStore.dayjs().toDate()
    }

    this._sendDate = {
      from: this._fromFilterDate,
      to: this._toFilterDate,
    }
  }

  rangeTriggerValue = () => {
    if (!this._fromFilterDate && this._selectedCalendarFilter === CalendarFilterEnum.allTime) {
      return CalendarFilterEnum.allTime
    }

    const to = uiStore.dayjs(this._toFilterDate, false).format(DayjsFormats.full6)
    const from = uiStore.dayjs(this._fromFilterDate, false).format(DayjsFormats.full6)

    return `${from} - ${to}`
  }

  resetSearchAndFilters = () => {
    this.setSelectedCalendarFilter(CalendarFilterEnum.allTime)
    this.setParamsDate()
  }

  updateCreatedAt = (createdAt: string | null) => {
    this._createdAt = createdAt
  }
}
